<template>
  <div>
    <breadcrumbs
      :breadcrumb-items="breadcrumbItems"
    />
    <UserToolbar
      v-if="account && Object.keys(account).length !== 0"
      :account="account"
    />
    <b-card>
      <SiteToolbar
        v-if="Object.keys(site).length !== 0"
        :site="site"
        closing
      />
    </b-card>

    <b-card class="card-statistics">
      <div class="card-statistics-tools">
        <div>
          <h4>{{ $t('Statistics') }}</h4>
        </div>
        <div class="d-flex align-items-center">
          <div class="col-picker">
            <div class="d-flex align-items-center">
              <feather-icon
                icon="CalendarIcon"
                size="16"
              />
              <flat-pickr
                v-model="rangePicker"
                :config="{mode: 'range', dateFormat: 'Y-m-d'}"
                class="form-control flat-picker bg-transparent border-0 shadow-none"
                placeholder="YYYY-MM-DD"
                @on-close="toggleRange"
              />
            </div>
          </div>
          <div v-if="false && site.advanced_stats_enabled">
            <b-button
              v-b-tooltip.hover.top="$t('sites.show_advanced_stats')"
              v-ripple.400="'rgba(40, 199, 111, 0.15)'"
              variant="outline-primary"
              size="sm"
              class="btn-icon"
              @click="toggleGraph()"
            >
              <feather-icon
                icon="BarChart2Icon"
                size="16"
              />
            </b-button>
          </div>
          <div v-if="authUserData.role.includes('admin')">
            <b-button
              v-ripple.400="'rgba(40, 199, 111, 0.15)'"
              variant="primary"
              size="sm"
              class="btn-icon ml-25"
              :to="{ name: 'dashboard-site-v2' }"
            >
              V2
            </b-button>
          </div>
        </div>
      </div>
      <div>
        <b-row
          v-if="statsData.length !== 0"
          class="row-statistics"
        >
          <b-col
            md="12"
            cols="12"
          >
            <statistic-tabs
              :card-title="$t('Statistics')"
              :stats-data="statsData"
              :account-currency="account.currency"
              @itemField="setField"
            />
          </b-col>
          <b-col
            md="12"
            cols="12 mt-3"
          >
            <SiteStatsTable
              :account-currency="account.currency"
              :stats-data="statsData"
            />
          </b-col>
        </b-row>
        <div
          v-else-if="graphicShow && statsData.length === 0"
          class="card-nodata"
        >
          <p class="card-text">
            {{ $t('sites.nodata_period') }}
          </p>
        </div>
      </div>
    </b-card>
    <div id="advanced_stats" />
    <div
      v-if="graphicShow && site.advanced_stats_enabled"
      id="blocks_stats"
    >
      <b-row v-if="statsBlocksLoading">
        <b-col>
          <b-card
            class="card-browser-states text-center"
          >
            <b-spinner label="Loading..." />
          </b-card>
        </b-col>
      </b-row>
      <b-row
        v-else-if="Object.keys(filterStatsBlocks).length !== 0 && !statsBlocksLoading"
        class="stats-blocks match-height"
      >
        <b-col
          v-if="filterStatsBlocks.imprByCountryCity.length > 1"
          md="4"
        >
          <countries-stats
            :subtitle="blockSubtitle"
            :country-city="filterStatsBlocks.imprByCountryCity"
          />
        </b-col>
        <b-col
          v-if="filterStatsBlocks.imprByBrowserName.length > 1"
          md="4"
        >
          <browser-stats
            :subtitle="blockSubtitle"
            :rows="filterStatsBlocks.imprByBrowserName"
          />
        </b-col>
        <b-col
          v-if="filterStatsBlocks.imprByLanguage.length > 1"
          md="4"
        >
          <language-stats
            :subtitle="blockSubtitle"
            :rows="filterStatsBlocks.imprByLanguage"
          />
        </b-col>
        <b-col
          v-if="filterStatsBlocks.imprByDeviceType.length > 1"
          md="4"
        >
          <devices-stats
            :subtitle="blockSubtitle"
            :rows="filterStatsBlocks.imprByDeviceType"
          />
        </b-col>
        <b-col
          v-if="filterStatsBlocks.imprByOsName.length > 1"
          md="4"
        >
          <os-stats
            :subtitle="blockSubtitle"
            :rows="filterStatsBlocks.imprByOsName"
          />
        </b-col>
        <b-col
          v-if="filterStatsBlocks.imprByPlatform.length > 1"
          md="4"
        >
          <platform-stats
            :subtitle="blockSubtitle"
            :rows="filterStatsBlocks.imprByPlatform"
          />
        </b-col>
        <b-col
          v-if="filterStatsBlocks.imprByUniqueUsers > 0"
          md="3"
        >
          <unique-users-stats
            :subtitle="blockSubtitle"
            :rows="filterStatsBlocks.imprByUniqueUsers"
          />
        </b-col>
        <b-col
          v-if="filterStatsBlocks.imprSiteByRequestUrl.length > 1"
          md="9"
        >
          <requests-stats
            :subtitle="blockSubtitle"
            :rows="filterStatsBlocks.imprSiteByRequestUrl"
          />
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import { getUserData } from '@/auth/utils'
import Ripple from 'vue-ripple-directive'
import getFormattedDate, { getFormattedDateMonth } from '@/utils/date-formatter'
import currencyFormatter from '@/utils/currency-formatter'
import useJwt from '@/auth/jwt/useJwt'
import {
  BRow,
  BCol,
  BCard,
  VBTooltip,
  BSpinner,
  BButton,
} from 'bootstrap-vue'
import Breadcrumbs from '@/views/ui/breadcrumbs/Breadcrumbs.vue'
import UserToolbar from '@/views/dashboard/users/UserToolbar.vue'
import SiteToolbar from '@/views/dashboard/sites/SiteToolbar.vue'
import StatisticTabs from '@/views/ui/stats-range-blocks/StatisticTabsLite.vue'
import CountriesStats from '@/views/ui/stats-range-blocks/CountriesStats.vue'
import BrowserStats from '@/views/ui/stats-range-blocks/BrowserStats.vue'
import LanguageStats from '@/views/ui/stats-range-blocks/LanguageStats.vue'
import DevicesStats from '@/views/ui/stats-range-blocks/DevicesStats.vue'
import OsStats from '@/views/ui/stats-range-blocks/OsStats.vue'
import RequestsStats from '@/views/ui/stats-range-blocks/RequestsStats.vue'
import PlatformStats from '@/views/ui/stats-range-blocks/PlatformStats.vue'
import UniqueUsersStats from '@/views/ui/stats-range-blocks/UniqueUsersStats.vue'
import flatPickr from 'vue-flatpickr-component'
import SiteStatsTable from './SitesStatsTable.vue'

export default {
  components: {
    BRow,
    BCol,
    Breadcrumbs,
    UserToolbar,
    BCard,
    StatisticTabs,
    BrowserStats,
    CountriesStats,
    DevicesStats,
    RequestsStats,
    LanguageStats,
    OsStats,
    PlatformStats,
    UniqueUsersStats,
    BSpinner,
    BButton,
    SiteToolbar,
    flatPickr,
    SiteStatsTable,
  },
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  data() {
    return {
      loading: true,
      statsBlocksLoading: false,
      pageTitle: '',
      pageSubtitle: '',
      breadcrumbItems: [],
      account: {},
      site: {},
      blockSubtitle: '',
      authUserData: getUserData(),
      currency: currencyFormatter,
      getFormattedDate,
      getFormattedDateMonth,
      statsData: [],
      statsBlocks: {
        show: {},
        click: {},
      },
      currentField: 'show',
      graphicShow: false,
      rangePicker: [getFormattedDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)), getFormattedDate(new Date())],
    }
  },
  computed: {
    filterStatsBlocks() {
      return this.statsBlocks[this.currentField] || {}
    },
    selectedRangeFormated() {
      let dateFrom
      let dateTo
      const selectedRange = this.rangePicker
      if (Array.isArray(selectedRange)) {
        [dateFrom, dateTo] = selectedRange
      } else {
        [dateFrom, , dateTo] = selectedRange.split(' ')
      }

      return {
        from: getFormattedDate(new Date(dateFrom)),
        to: dateTo === undefined ? getFormattedDate(new Date(dateFrom)) : getFormattedDate(new Date(dateTo)),
      }
    },
  },
  watch: {
    currentField(newValue) {
      this.statsBlocksLoading = true
      setTimeout(() => {
        this.statsBlocksLoading = false
      }, 500)

      if (newValue === 'amount') {
        this.graphicShow = false
      }
    },
  },
  created() {
    this.getUserData()
    this.getSiteData()
    this.getStatsData()
    // this.getSiteClickesStats()
    this.blockSubtitle = `${this.selectedRangeFormated.from.replaceAll('-', '.')}-${this.selectedRangeFormated.to.replaceAll('-', '.')}`
  },
  methods: {
    scrollToTop(selector) {
      const rootEle = document.documentElement
      const element = document.querySelector(selector)
      const elementOffsetTop = element.offsetTop

      setTimeout(() => {
        rootEle.scrollTo({
          top: elementOffsetTop - 200,
          behavior: 'smooth',
        })
      }, 500)
    },
    toggleRange(e) {
      this.rangePicker = e
      this.blockSubtitle = `${this.selectedRangeFormated.from.replaceAll('-', '.')}-${this.selectedRangeFormated.to.replaceAll('-', '.')}`
      this.getStatsData()
      if (this.graphicShow) {
        this.getSiteEstats()
      }
      // this.getSiteClickesStats()
    },
    toggleGraph() {
      this.graphicShow = !this.graphicShow
      if (this.graphicShow) {
        this.getSiteEstats()
        const firstTab = document.querySelector('.nav-tabs > li > a')
        if (firstTab && this.currentField === 'amount') {
          firstTab.click()
        }
        this.scrollToTop('#advanced_stats')
      }
    },
    async getSiteEstats() {
      this.statsBlocksLoading = true
      this.statsBlocks.show = {}
      const params = this.selectedRangeFormated
      const responseData = await useJwt.getSiteEStats(this.$route.params.siteId, params)
      this.statsBlocks.show = responseData.data.siteesstats
      this.statsBlocksLoading = false
    },
    async getSiteClickesStats() {
      this.statsBlocksLoading = true
      this.statsBlocks.click = {}
      const params = this.selectedRangeFormated
      const responseData = await useJwt.getSiteClickesStats(this.$route.params.siteId, { from: params.from, to: params.to })
      this.statsBlocks.click = responseData.data.siteclickesstats
      this.statsBlocksLoading = false
    },
    async getUserData() {
      const responseData = await useJwt.getUser(this.$route.params.userId)
      this.account = responseData.data.account || {}
      this.pageTitle = this.account.name
      this.setBreabcrumbs()
    },
    async getSiteData() {
      const responseData = await useJwt.getSite(this.$route.params.siteId)
      this.site = responseData.data.site || {}
      this.setBreabcrumbs()
    },
    setBreabcrumbs() {
      this.breadcrumbItems = [
        {
          text: this.$t('page.menu.users'),
          to: '/users',
        },
        {
          text: this.pageTitle,
          to: `/users/${this.$route.params.userId}`,
        },
        {
          text: this.$t('page.menu.sites'),
          to: `/users/${this.$route.params.userId}/sites`,
        },
        {
          text: this.site.title,
          active: true,
        },
      ]
    },
    async getStatsData() {
      this.loading = true
      this.statsData = []
      const params = {
        site_id: this.$route.params.siteId,
        type: 'custom',
      }

      const reqParams = Object.assign(params, this.selectedRangeFormated)
      const responseData = await useJwt.getSiteStatsByType(reqParams)
      this.statsData = responseData.data.sitestats || []
      this.loading = false
    },
    setField(field) {
      this.currentField = field
    },
  },
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>

<style scoped lang="scss">
.card-statistics-tools {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

@media (max-width: 440px) {
  [dir] #app {
    .card-statistics-tools {
      flex-wrap: wrap;
    }
  }
}
</style>
